import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination/Pagination';
import { LANG_TEXT } from '../../constants/language';
import { useAuthContext } from '../../context/AuthContext/AuthContext';
import { useOrderContext } from '../../context/OrderContext';
import { useOrderStadiumContext } from '../../context/OrderStadiumContext';
import { useSettingsContext } from '../../context/SettingsContext';
import usePermission from '../../hooks/usePermission';
import { usePreorderEnabled } from '../../hooks/usePreorderEnabled';
import { DamagedItemStatus, OrderItemStatus, OrderSummaryData } from '../../interfaces/index.types';
import { getEndOfDay, getStartOfDay } from '../../utils/handleDateTime';
import { parseOrderList } from '../../utils/orderHistory.utils';
import { OrderType } from '../Dashboard/order.types';
import DatePickerCustom from './components/DatePicker/DatePicker';
import DownloadCsv from './components/DownloadCsv/DownloadCsv';
import Loader from './components/Loader/Loader';
import TableBodyCustom from './components/TableBody/TableBody';
import TableHeadCustom from './components/TableHead/TableHead';
import TableSummary from './components/TableSummary/TableSummary';
import { IOrderHistory } from './orderHistory.types';
import { useStyles } from './styles';

interface OrderHistoryProps {
  orderType?: OrderType;
}

export default function OrderHistory({ orderType }: OrderHistoryProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const { userInfo } = useAuthContext();
  const { showForAdmin, showForSuperAdmin } = usePermission();
  const orderContextOfStadium = useOrderStadiumContext();
  const orderContextOfRestaurant = useOrderContext();
  const { currentSetting } = useSettingsContext();
  const { enabledNewVersion } = usePreorderEnabled();

  const { allOrders, loading, seatDataMap, riderMap } = useMemo(() => {
    if (showForSuperAdmin || showForAdmin) {
      return {
        allOrders: orderContextOfStadium.allOrders,
        loading: orderContextOfStadium.loading,
        seatDataMap: orderContextOfStadium.seatDataMap,
        riderMap: orderContextOfStadium.riderMap,
      };
    } else {
      return {
        allOrders: orderContextOfRestaurant.allOrders,
        loading: orderContextOfRestaurant.loading,
        seatDataMap: orderContextOfRestaurant.seatDataMap,
        riderMap: orderContextOfRestaurant.riderMap,
      };
    }
  }, [
    showForSuperAdmin,
    orderContextOfStadium.allOrders,
    orderContextOfStadium.loading,
    orderContextOfStadium.seatDataMap,
    orderContextOfStadium.riderMap,
    orderContextOfRestaurant.allOrders,
    orderContextOfRestaurant.loading,
    orderContextOfRestaurant.seatDataMap,
    orderContextOfRestaurant.riderMap,
  ]);

  const prefix = '_DAMAGED';
  const [page, setPage] = useState<number>(0);
  const rowsPerPage = 10;
  const [dateFilter, setDateFilter] = useState<{ start: Date; end: Date }>(() => {
    const today = new Date();
    return {
      start: getStartOfDay(today),
      end: getEndOfDay(today),
    };
  });
  const [selectedFoodTrucks, setSelectedFoodTrucks] = useState<string[]>([]);
  const [dateFilterType, setDateFilterType] = useState<'createTime' | 'deliveryTime'>('createTime');

  const ORDER_TYPE_TITLES: Record<OrderType, string> = {
    [OrderType.REGULAR_ORDERS]: t(LANG_TEXT.REGULAR_ORDERS),
    [OrderType.REGULAR_PRE_ORDERS]: t(LANG_TEXT.REGULAR_PRE_ORDERS),
    [OrderType.SPECIAL_ORDERS]: t(LANG_TEXT.SPECIAL_ORDERS),
    [OrderType.SPECIAL_PRE_ORDERS]: t(LANG_TEXT.SPECIAL_PRE_ORDERS),
  };
  const pageTitle = orderType ? ORDER_TYPE_TITLES[orderType] : t(LANG_TEXT.ORDER_HISTORY);

  const handleDateSelect = (start: Date | null, end: Date | null) => {
    if (start && end) {
      setDateFilter({ start, end });
      setPage(0);
    }
  };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const [displayedOrders, setDisplayedOrders] = useState<IOrderHistory[]>([]);

  useEffect(() => {
    const loadOrders = async () => {
      const parsedOrders = await parseOrderList(allOrders, userInfo, currentSetting, seatDataMap, t, riderMap || {}, enabledNewVersion);

      const filteredByDate = parsedOrders.filter((order) => {
        let orderTime: Date;
        if (dateFilterType === 'deliveryTime' && order.requested_delivery_time) {
          orderTime = new Date(order.requested_delivery_time.seconds * 1000);
        } else {
          orderTime = new Date(order.order_create_time.seconds * 1000);
        }
        const dateMatch = orderTime >= getStartOfDay(dateFilter.start) && orderTime <= getEndOfDay(dateFilter.end);
        return dateMatch && (!orderType || order.order_type === orderType);
      });

      if (selectedFoodTrucks.length === 0) {
        setDisplayedOrders(filteredByDate);
        return;
      }

      setDisplayedOrders(
        filteredByDate.filter((order) => {
          const singleOrder = order.restaurant_id && selectedFoodTrucks.includes(order.restaurant_id);
          const multipleOrder = order.multi_orders && order.multi_orders.some((item) => selectedFoodTrucks.includes(item.restaurant_id));
          return singleOrder || multipleOrder;
        })
      );
    };

    loadOrders();
  }, [allOrders, userInfo, currentSetting, seatDataMap, t, riderMap, dateFilter, orderType, selectedFoodTrucks, dateFilterType, enabledNewVersion]);

  const handleFoodTruckFilterChange = (selected: string[]) => {
    setSelectedFoodTrucks(selected);
    setPage(0);
  };

  const orderSummary = useMemo<{ [key: string]: OrderSummaryData | null }>(() => {
    const calculateOrderStats = (orders: IOrderHistory[]): OrderSummaryData => {
      const totalOrders = new Set(orders.map((order) => order.id)).size;

      const totalDeliveryFee = orders.filter((order) => order.item_food_id === t(LANG_TEXT.DELIVERY_FEE_ETC)).reduce((sum, order) => sum + (order.item_price ?? 0), 0);

      const totalOrderAmount = orders.reduce((sum, order) => sum + (order.item_price ?? 0) * (order.item_quantity ?? 0), 0);
      const totalDamagedFee = orders
        .filter((order) => order.id.includes(prefix))
        .reduce((sum, order) => sum + (order.item_price ?? 0) * (order.item_quantity ?? 0), 0);

      const totalFoodRevenue = totalOrderAmount - totalDeliveryFee;
      const totalWithServiceCharge = totalFoodRevenue * 0.1;
      const totalBenefit = totalDeliveryFee + totalWithServiceCharge;

      const uniqueOrderIds = Array.from(new Set(orders.map((order) => order.id)));

      const cancelledOrderIds = new Set(orders.filter((order) => order.order_status === OrderItemStatus.CANCELLED).map((order) => order.id));
      const totalCancelledOrder = uniqueOrderIds.filter((id) => cancelledOrderIds.has(id)).length;

      const deliveredOrderIds = new Set(
        orders
          .filter((order) => order.order_status !== OrderItemStatus.CANCELLED && order.order_status !== DamagedItemStatus.DAMAGED_CANCELLED && !order.id.includes(prefix))
          .map((order) => order.id)
      );
      const totalDeliveredOrder = uniqueOrderIds.filter((id) => deliveredOrderIds.has(id)).length;
      const totalDamagedOrder = uniqueOrderIds.filter((id) => id.includes(prefix)).length;

      return {
        totalOrders,
        totalDeliveryFee,
        totalOrderAmount,
        totalFoodRevenue,
        totalDamagedFee,
        totalWithServiceCharge,
        totalBenefit,
        totalDeliveredOrder,
        totalDamagedOrder,
        totalCancelledOrder,
      };
    };

    if (orderType) {
      return {
        [orderType]: calculateOrderStats(displayedOrders),
      };
    } else {
      const regularOrders = displayedOrders.filter((order) => order.order_type === OrderType.REGULAR_ORDERS);
      const regularPreOrders = displayedOrders.filter((order) => order.order_type === OrderType.REGULAR_PRE_ORDERS);
      const specialOrders = displayedOrders.filter((order) => order.order_type === OrderType.SPECIAL_ORDERS);
      const specialPreOrders = displayedOrders.filter((order) => order.order_type === OrderType.SPECIAL_PRE_ORDERS);

      return {
        regular_order: calculateOrderStats(regularOrders),
        regular_pre_order: calculateOrderStats(regularPreOrders),
        special_order: calculateOrderStats(specialOrders),
        special_pre_order: calculateOrderStats(specialPreOrders),
      };
    }
  }, [displayedOrders, orderType, t, enabledNewVersion]);

  useEffect(() => {
    setSelectedFoodTrucks([]);
  }, [allOrders, orderType]);

  return (
    <Box
      component="main"
      className={classes.Dashboard}
      sx={{
        backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900]),
      }}
    >
      <Box className={classes.BoxTitle}>
        <Typography fontSize={24} lineHeight={'32px'} fontWeight={500} color={'var(--Gray-900, #101828)'}>
          {pageTitle}
        </Typography>

        {!loading && (
          <Box className={classes.BoxDownload}>
            <RadioGroup value={dateFilterType} onChange={(e) => setDateFilterType(e.target.value as 'createTime' | 'deliveryTime')} className={classes.RadioGroup}>
              <FormControlLabel value="createTime" control={<Radio size="small" />} label={t(LANG_TEXT.SEARCH_BY_ORDER_TIME)} />
              <FormControlLabel value="deliveryTime" control={<Radio size="small" />} label={t(LANG_TEXT.SEARCH_BY_DELIVERY_TIME)} />
            </RadioGroup>
            <DatePickerCustom onSelect={handleDateSelect} />
            <DownloadCsv
              csvData={displayedOrders}
              seatDataMap={seatDataMap}
              showForSuperAdmin={showForSuperAdmin}
              showForAdmin={showForAdmin}
              summaryData={orderSummary}
              routeType="order_history"
            />
          </Box>
        )}
      </Box>
      {loading ? (
        <Loader />
      ) : (
        <Paper className={classes.Table}>
          <TableContainer className={classes.Scrollbar}>
            <Table stickyHeader aria-label="sticky table" sx={{ borderCollapse: 'collapse' }}>
              <TableHeadCustom style={classes.Table} showForSuperAdmin={showForSuperAdmin} />
              <TableBodyCustom data={displayedOrders} rowsPerPage={rowsPerPage} page={page} showForSuperAdmin={showForSuperAdmin} />
            </Table>
          </TableContainer>
          <Box className={classes.BoxSummary}>
            <TableSummary
              style={classes.Table}
              tableCell={classes.TableCell}
              showForSuperAdmin={showForSuperAdmin}
              data={orderSummary}
              routeType="order_history"
              selectedFoodTrucks={selectedFoodTrucks}
              onFilterChange={handleFoodTruckFilterChange}
            />
          </Box>
          <Pagination data={displayedOrders} rowsPerPage={rowsPerPage} onPageChange={onPageChange} />
        </Paper>
      )}
    </Box>
  );
}
